import type React from "react";
import { createPortal } from "react-dom";
import { useVisualViewportBottomInset } from "../../hooks/use-visual-viewport-inset";
import { cn } from "../../lib/utils";

type KeyboardDockProps = React.ComponentPropsWithoutRef<"div"> & {
  /**
   * Where the dock mounts. Defaults to the document body: the dock rides the
   * page's own viewport. A host that IS a viewport (a framed screen) passes
   * its body so the dock docks to that screen's bottom edge instead.
   */
  portalProps?: { container?: HTMLElement | null };
};

/**
 * A bar docked to the bottom of the visual viewport — directly above the
 * software keyboard while one is up, on the screen's bottom edge otherwise.
 * The platform's own input-accessory grammar: the controls belong to the
 * keyboard, not to the field being typed into, so they never scroll with the
 * content and never fight the field's host for room.
 *
 * Body-portaled and `position: fixed`, so it sits outside every scroll
 * container and above every overlay tier (drawers and dialogs stop at z-50,
 * toasts at z-60 — the dock rides the keyboard, which is above them all).
 * Pointer events are re-enabled explicitly: a modal host sets
 * `pointer-events: none` on <body>, which a body portal would inherit.
 *
 * One dock per page. Only one field holds focus at a time, so the host that
 * owns the focused field is the one that mounts it.
 */
export function KeyboardDock({
  className,
  style,
  children,
  portalProps,
  ...props
}: KeyboardDockProps): React.ReactElement | null {
  // Zero under `interactive-widget=resizes-content` and on every browser that
  // shrinks the layout viewport for the keyboard — there `bottom: 0` already
  // lands on the keyboard's top edge. Positive on `resizes-visual` browsers
  // (iOS Safari), where the layout viewport keeps its height under the keys.
  const keyboardInset = useVisualViewportBottomInset();
  if (typeof document === "undefined") return null;
  const container = portalProps?.container ?? document.body;
  return createPortal(
    <div
      data-slot="keyboard-dock"
      className={cn(
        "pointer-events-auto fixed inset-x-0 z-70 border-t border-border bg-background",
        // The home-indicator inset applies only while the dock rests on the
        // screen edge: a raised keyboard covers that band.
        keyboardInset === 0 && "pb-[env(safe-area-inset-bottom,0px)]",
        className,
      )}
      style={{ bottom: keyboardInset, ...style }}
      {...props}
    >
      {children}
    </div>,
    container,
  );
}
